import _ from 'underscore';
import { PureKnockoutView } from '@bingads-webui/knockout-view';
import tmplBulkEdit from './template.pug';
import ViewModel from './viewmodel';

/**
 * CustomParametersView
 * Trigger event customParameters:updated when user input update
 */
export class CustomParametersView extends PureKnockoutView {
  /**
   * Initilize custom prameters view settings
   *
   * @param {Object} param - An object containing these default options for view:
   * @param {Object} param.i18n - the localization settings
   * @param {Object} param.i18n.getString - localization settings function
   * @param {boolean} param.editMode - whether the view is in edit mode, default true
   * @param {boolean} param.bulkEditMode - whether in bulk edit mode, default false
   * @param {boolean} param.bulkEditShowAddLabel - whether show add label,
   *  only valid in bulk edit mode
   * @param {boolean} param.bulkEditShowMatchCase - whether show match case option,
   *  only valid in bulk edit mode
   * @param {String[]} param.errorMessages - error messages to show
   * @param {Object[]} param.customParameters - initial custom params,
   *   in the format of {Key, Value}
   * @param {boolean} param.forceUseIncreasedCount - if `true`, will disregard the pilot flags and
   * use the increased number of custom parameters. Otherwise, will follow the current
   * pilot+entityType logic to determine the enablement of the increased count.
   * @returns {void} void
  */
  initialize({
    i18n,
    editMode = true,
    bulkEditMode = false,
    bulkEditShowAddLabel = true,
    bulkEditShowMatchCase = false,
    errorMessages,
    customParameters = [],
    permissions,
    entityType,
    forceUseIncreasedCount,
    withRealTimeUpdates = false,
    readOnly = false,
  }) {
    this.options = {
      i18n,
      editMode,
      bulkEditMode,
      bulkEditShowAddLabel,
      bulkEditShowMatchCase,
      customParameters,
      template: tmplBulkEdit,
      errorMessages,
      permissions,
      entityType,
      forceUseIncreasedCount,
      readOnly,
    };

    super.initialize(this.options);

    // trigger customParameters:updated when user input update
    // isValid is computed based on customerParameters
    this.viewModel.isValid.subscribe(() => {
      this.trigger('customParameters:updated', { isValid: this.isValid(), customParameters: this.getValues() });
    }, this);

    if (withRealTimeUpdates) {
      this.viewModel.parameterArray.subscribe((val) => {
        const newVal = _.filter(val, item => !_.isEmpty(item.Key) || !_.isEmpty(item.Value));

        this.trigger('change:customParameters', { isValid: this.isValid(), customParameters: newVal });
      });
    }
  }

  /**
   * Go to add replace mode, only meaningful in bulk edit mode
   * It will hide 'match case' option, and show 'add another' link
   *
   * @returns {void} void
  */
  goToAddReplaceMode() {
    // those two only used in bulk edit mode
    this.viewModel.bulkEditShowAddLabel(true);
    this.viewModel.bulkEditShowMatchCase(false);
  }

  /**
   * Go to add remove mode, only meaningful in bulk edit mode
   * It will show 'match case' option, and hide 'add another' link
   * It will also only show the first parameter
   *
   * @returns {void} void
  */
  goToRemoveMode() {
    this.viewModel.bulkEditShowAddLabel(false);
    this.viewModel.bulkEditShowMatchCase(true);
    if (this.options.bulkEditMode) {
      this.viewModel.parameters(this.viewModel.parameters.slice(0, 1));
    }
  }

  get ViewModel() {
    return ViewModel;
  }

  get template() {
    return () => this.options.template({
      i18n: this.options.i18n,
      bulkEditMode: this.options.bulkEditMode,
    });
  }

  /**
   * Set edit mode
   * @param {boolean} isEdit - whether to set to edit (false to be read mode)
   *
   * @returns {void} void
  */
  setEditMode(isEdit) {
    this.viewModel.editMode(isEdit);
  }

  /**
   * set Disabled value
   * @param {boolean} disabled - whether to set to disabled (false to be editable mode)
   *
   * @returns {void} void
  */
  setDisabled(disabled) {
    this.viewModel.isDisabled(disabled);
  }

  isValid() {
    return this.viewModel.isValid();
  }

  /**
   * Get custom parameters values
   * @param {boolean} separatedKeyValueProperties - whether to seperate key value properties
   * when true, return result as format of [{Key:name, Value: value}]
   * when false, else return it as [{name:value}] format.
   * For bulk edit mode, it will return them as {params, matchCase};
   *
   * @returns {Object} parameters value
  */
  getValues(separatedKeyValueProperties) {
    const list = [];
    _.each(this.viewModel.parameters(), (p) => {
      const key = p.key();

      if (key) {
        const item = {};
        let val = p.val();

        if (val === undefined) {
          val = '';
        }
        if (separatedKeyValueProperties) {
          item.Key = key;
          item.Value = val;
        } else {
          item[key] = val;
        }
        list.push(item);
      }
    });

    if (this.options.bulkEditMode) {
      return {
        params: list,
        matchCase: this.viewModel.bulkEditMatchCaseSelected(),
      };
    }
    return list;
  }
}

